// Imports
import React, { useCallback, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import TableContainer from "../../../../components/Common/TableContainer";
import ReportExcelExport from "../../../../components/Common/ReportExcelExport"
import { loadReportByDateRange as onLoadReportByDateRange } from "../../../../slice/thunks";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import GlobalLoader from '../../../../components/Common/GlobalLoader';
import { convertToDate } from "../../../../helpers/historyFile";


const TrialBalance = ({ t = (key) => key }) => {
  const dispatch = useDispatch();

  const selectLayoutState = (state) => state.Outputs;
  const OutputsProperties = createSelector(
    selectLayoutState,
    (outputs) => ({
      reports: outputs.reports
    })
  );
  const { reports } = useSelector(OutputsProperties);

  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [localStartDate, setLocalStartDate] = useState(null)
  const [localEndDate, setLocalEndDate] = useState(null)

  const loadReportsByDateRange = useCallback((startDate, endDate) => {
    if (startDate && endDate) {
      setIsLoading(true);
      dispatch(onLoadReportByDateRange({
        startDate: startDate,
        endDate: endDate,
      })).finally(() => {
        setIsLoading(false);
      });
    }
  }, [dispatch]);

  const handleDateSearch = () => {
    if (dateRange.length === 2) {
      setLocalEndDate(dateRange[1])
      setLocalStartDate(dateRange[0])
      loadReportsByDateRange(convertToDate(dateRange[0]), convertToDate(dateRange[1]));
    }
  };

  const columns = useMemo(() => [
    {
      header: t("account_number"),
      accessorKey: "code",
      style: "text-start",
      enableColumnFilter: false,
    },
    {
      header: t("description"),
      accessorKey: "name",
      style: "text-start",
      enableColumnFilter: false,
    },
    {
      header: t("debit"),
      accessorKey: "debit",
      style: "text-start",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { debit = 0, credit = 0 } = row.original;
        const value = debit > credit ? debit - credit : 0;
        return value ? value.toLocaleString() : "-";
      },
    },
    {
      header: t("credit"),
      accessorKey: "credit",
      style: "text-start",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { debit = 0, credit = 0 } = row.original;
        const value = credit > debit ? credit - debit : 0;
        return value ? value.toLocaleString() : "-";
      },
    },
  ], [t]);

  const totalDebit = useMemo(() => {
    return reports?.reduce((sum, { debit = 0, credit = 0 }) => {
      return sum + (debit > credit ? debit - credit : 0);
    }, 0) || 0;
  }, [reports]);

  const totalCredit = useMemo(() => {
    return reports?.reduce((sum, { debit = 0, credit = 0 }) => {
      return sum + (credit > debit ? credit - debit : 0);
    }, 0) || 0;
  }, [reports]);

  return (
    <React.Fragment>
      {isLoading ? (
        <GlobalLoader />
      ) : (
        <div className="card trial-balance-content border-0 p-0 m-0" id="table2">
          <div className="container-fluide">
            <div className="row">
              <div className="pt-1">
                <div className="d-flex justify-content-between align-items-center mb-2 px-3">
                  <div className="d-flex align-items-center gap-2">
                    <Flatpickr
                      className="form-control"
                      options={{ mode: "range", dateFormat: "d-m-Y" }}
                      placeholder={t("select_date_range")}
                      value={dateRange}
                      onChange={setDateRange}
                      style={{
                        border: "1px solid #ccc",
                        margin: "7px",
                        marginTop: "10px",
                      }}
                    />
                    <button className="btn btn-primary d-inline-flex align-items-center" onClick={handleDateSearch}>
                      <i className="fa fa-search me-1" />{t("Search")}
                    </button>
                      {/* Appel du composant ReportExcelExport */}
                    <ReportExcelExport
                      title="Trial"
                      data={reports}
                      columnsOrder={["code", "name", "debit", "credit"]}
                      totalFields={["debit", "credit"]}
                      dateRange={{ start: localStartDate, end: localEndDate }}
                    />
                  </div>
                </div>

                <TableContainer
                  columns={columns}
                  data={reports || []}
                  isGlobalFilter={false}
                  divClass="table-card-report"
                  tableClass="align-middle"
                  theadClass="thead-light table-light"
                  footer={
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan="2" className="text-end fw-bold">{t("total")}</td>
                        <td className="fw-bold">{totalDebit.toLocaleString()}</td>
                        <td className="fw-bold">{totalCredit.toLocaleString()}</td>
                      </tr>
                    </tfoot>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default withTranslation()(TrialBalance);
